import React, { useMemo, useRef, useState } from "react";
import { diffMin, addMinutes, isoNoMs } from "@/lib/format";
import {
  useGridIndexes,
  useFilteredBlocks,
  useGridRows,
  useCrewTimelines,
} from "@/hooks/useGridIndexes";
import { useColumnWidths } from "@/hooks/useColumnWidths";
import { useBlockDrag } from "@/hooks/useBlockDrag";
import { useNowLine } from "@/hooks/useNowLine";
import { useCrewOrder } from "@/hooks/useCrewOrder";
import { useVirtualRows } from "@/hooks/useVirtualRows";

import GridHeader from "./grid/GridHeader";
import DateBand from "./grid/DateBand";
import RoomBand from "./grid/RoomBand";
import { SUBTASK_SOURCE_MIME } from "./grid/roomDnd";
import NewTaskRow from "./grid/NewTaskRow";
import FloatingTaskRow from "./grid/FloatingTaskRow";
import Row from "./grid/Row";
import SubtaskRow from "./grid/SubtaskRow";
import NowLineOverlay from "./grid/NowLineOverlay";
import DragBadge from "./grid/DragBadge";
import { DENSITY_HEIGHT } from "./grid/rowUtils";

/**
 * Schedule Grid — time-ordered run-sheet table.
 * - Columns: Start | Finish | Room | Task | Status | Notes/Blockers | …crew columns
 * - Inline edit on every cell
 * - Drag row by left handle to shift time; drag-resize bottom for duration
 * - Multi-select with shift / ctrl, bulk action bar at bottom when ≥2 selected
 * - Right-click any row → cell history drawer
 *
 * This component is intentionally thin — most of its responsibilities are
 * delegated to hooks (data indexing, column widths, drag, now-line) and
 * presentational sub-components under ./grid/.
 */
export default function ScheduleGrid({
  project,
  rooms,
  crew,
  tasks,
  blocks,
  assignments,
  blockers,
  filters,
  density,
  selection,
  setSelection,
  onPatchBlock,
  onPatchTask,
  onAssignCrew,
  onAssignCrewToTask,
  onUnassignCrew,
  doubleBookKeys,
  onOpenHistory,
  onSelectTask,
  onEditNotes,
  onDuplicateTask,
  onDeleteTask,
  onDropFloating,
  onDropFloatingForDay,
  onMoveSelectionToRoom,
  onGroupTasks,
  onUnparentSubtasks,
  selectionRef,
  onOpenDaySettings,
  onNewTaskForDay,
  onQuickAddTask,
  onNewFloatingForRoom,
  onQuickAddFloatingForRoom,
  groupMode = "date",
  floatingTasks = [],
  schedFilter = "all",
  onAddCrewRole,
}) {
  const rowH = DENSITY_HEIGHT[density] || 38;
  const containerRef = useRef(null);

  // Per-user crew column order (localStorage). Reordering happens here; all
  // downstream visibleCrew etc. derive from `orderedCrew` so the grid header,
  // crew cells, and timelines all see the same sequence.
  const { orderedCrew, moveCrew } = useCrewOrder(project?.id, crew);

  // Drop-line overlay state — used for both crew column reorder AND for
  // dragging a floating task in. `kind` distinguishes the two so the line can
  // span the right axis (vertical for column reorder, horizontal for row
  // placement).
  const [dropLine, setDropLine] = useState(null); // { x?, y?, kind }
  const dragKindRef = useRef(null); // "crew-col" | "floating-row" | null
  const crewDragFromIdxRef = useRef(null);

  // Expanded parent tasks (those whose subtask rows are visible).
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (taskId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  // ── Index lookups ──────────────────────────────────────────
  const { roomById, taskById, crewById, subtasksByParent, assignmentsByBlock, blockersByTask } =
    useGridIndexes({ rooms, tasks, crew: orderedCrew, assignments, blockers });

  // ── Filter + sort blocks ───────────────────────────────────
  const filteredBlocks = useFilteredBlocks({ blocks, taskById, assignmentsByBlock, filters });

  // ── Adaptive crew columns ──────────────────────────────────
  // By default we auto-hide crew with no assignments in the current window
  // (keeps the grid scannable on large projects). When `filters.showAllCrew`
  // is true, every crew column is shown so producers can assign people who
  // aren't yet on the schedule.
  const visibleCrew = useMemo(() => {
    if (filters?.showAllCrew) return orderedCrew;
    if (orderedCrew.length <= 8) return orderedCrew;
    const inWindow = new Set();
    for (const b of filteredBlocks) {
      for (const a of assignmentsByBlock[b.id] || []) {
        inWindow.add(a.crew_member_id);
      }
    }
    return orderedCrew.filter((c) => inWindow.has(c.id));
  }, [orderedCrew, filteredBlocks, assignmentsByBlock, filters?.showAllCrew]);

  // ── Build rows (date bands + new-task affordance + blocks + subtasks) ──
  const rows = useGridRows({ filteredBlocks, subtasksByParent, expanded, project, groupMode, rooms, floatingTasks, schedFilter });

  // ── Virtualizer ────────────────────────────────────────────
  // For projects with >200 rendered rows, only mount the rows currently in
  // viewport (± overscan). Spacers reserve the scroll height. Sticky
  // left columns are preserved because windowed rows still emit cells into
  // the parent CSS-grid as before.
  const { visibleStart, visibleEnd, topPx, bottomPx, enabled: virtEnabled } =
    useVirtualRows({ rows, density, scrollRef: containerRef });

  // Per-crew timelines for room-transfer arrows + free-gap labels in crew cells.
  const crewTimelines = useCrewTimelines({ blocks, assignments });

  // ── Column widths (persisted) ──────────────────────────────
  const { fixedWidths, crewWidthFor, fixedTotal, leftOffsets, beginColResize } = useColumnWidths();
  // Per-crew widths so each column can be resized individually.
  const crewWidths = visibleCrew.map((c) => crewWidthFor(c.id));
  const gridTemplate = `${fixedWidths.map((w) => `${w}px`).join(" ")} ${crewWidths.map((w) => `${w}px`).join(" ")}`;
  const crewTotal = crewWidths.reduce((s, w) => s + w, 0);

  // ── Drag-to-move + drag-to-resize ──────────────────────────
  const { dragState, resizeState, beginRowDrag, beginResize } = useBlockDrag({ blocks, onPatchBlock });

  // ── Now line ───────────────────────────────────────────────
  const nowRowIndex = useNowLine(rows);

  // ── Selection ──────────────────────────────────────────────
  // Selection keys: block IDs (raw uuid) for scheduled blocks, OR "ft:{taskId}"
  // for floating task rows. Range-select (shift-click) traverses the combined
  // visible row list so producers can lasso a mix.
  const selectableKeys = useMemo(
    () => rows
      .filter((r) => r.kind === "block" || r.kind === "floating-task")
      .map((r) => (r.kind === "block" ? r.block.id : `ft:${r.task.id}`)),
    [rows]
  );

  const handleSelect = (key, e) => {
    const ids = new Set(selection);
    if (e.shiftKey && selection.length > 0) {
      const anchor = selection[selection.length - 1];
      const aIdx = selectableKeys.indexOf(anchor);
      const bIdx = selectableKeys.indexOf(key);
      if (aIdx !== -1 && bIdx !== -1) {
        const [lo, hi] = aIdx < bIdx ? [aIdx, bIdx] : [bIdx, aIdx];
        setSelection(selectableKeys.slice(lo, hi + 1));
      }
      return;
    }
    if (e.metaKey || e.ctrlKey) {
      if (ids.has(key)) ids.delete(key);
      else ids.add(key);
      setSelection([...ids]);
      return;
    }
    setSelection([key]);
  };

  // ── Floating-task drop targets ─────────────────────────────
  const handleRowDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleRowDrop = (block, e) => {
    e.preventDefault();
    try {
      const payload = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (payload.kind === "floating-task" && payload.task_id) {
        onDropFloating && onDropFloating(payload.task_id, block);
      }
    } catch (err) {
      console.warn("Floating-task drop payload could not be parsed", err);
    }
  };

  // ── Render ─────────────────────────────────────────────────
  // Now 7 fixed columns (added DUR between FINISH and ROOM).
  const colSpan = 7 + visibleCrew.length;

  /* ── Crew column drag-and-drop ─────────────────────────────
     The HTML5 drag API is used so the crew column header is the drag source
     and the grid wrapper is the drop target. We track the cursor X in the
     wrapper to draw a vertical drop-line between columns. */
  const onCrewDragStart = (idx, e) => {
    dragKindRef.current = "crew-col";
    crewDragFromIdxRef.current = idx;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", JSON.stringify({ kind: "crew-col", idx }));
  };
  const onCrewDragEnd = () => {
    dragKindRef.current = null;
    crewDragFromIdxRef.current = null;
    setDropLine(null);
  };
  const onCrewDragOver = (idx, e) => {
    if (dragKindRef.current !== "crew-col") return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    // Snap line to the side of the hovered column nearest the cursor.
    const rect = e.currentTarget.getBoundingClientRect();
    const wrapRect = containerRef.current?.getBoundingClientRect();
    if (!wrapRect) return;
    const beforeMidpoint = e.clientX < rect.left + rect.width / 2;
    const x = (beforeMidpoint ? rect.left : rect.right) - wrapRect.left + (containerRef.current?.scrollLeft || 0);
    setDropLine({ x, kind: "crew-col" });
  };
  const onCrewDrop = (idx, e) => {
    if (dragKindRef.current !== "crew-col") return;
    e.preventDefault();
    const fromIdx = crewDragFromIdxRef.current;
    if (fromIdx == null) return;
    // Resolve before/after based on which half of the column the cursor is in.
    const rect = e.currentTarget.getBoundingClientRect();
    const after = e.clientX >= rect.left + rect.width / 2;
    const toIdx = after ? idx + 1 : idx;
    moveCrew(fromIdx, toIdx);
    onCrewDragEnd();
  };

  /* ── Floating-task drag-over for the wrapper (horizontal drop-line) ──
     The `.scheduleFloatingAtDay` drop is handled by `DateBand`. This handler
     ONLY draws the visual horizontal line so users see where their task will
     land while dragging across the grid. */
  const onWrapperDragOver = (e) => {
    const types = e.dataTransfer?.types;
    if (!types) return;
    // A floating-task drag carries a JSON payload; detect via dataTransfer
    // types (the actual data isn't readable here for security reasons).
    const isFloating = Array.from(types).some((t) => t === "text/plain");
    if (!isFloating || dragKindRef.current === "crew-col") return;
    const wrapRect = containerRef.current?.getBoundingClientRect();
    if (!wrapRect) return;
    const y = e.clientY - wrapRect.top + (containerRef.current?.scrollTop || 0);
    setDropLine({ y, kind: "floating-row" });
  };
  const onWrapperDragLeave = (e) => {
    // Only clear when the cursor actually leaves the wrapper, not its children.
    if (e.currentTarget === e.target) setDropLine(null);
  };

  return (
    <div
      className="relative flex-1 bg-bg-base"
      ref={containerRef}
      data-testid="schedule-grid"
      onDragOver={(e) => {
        // Allow subtask unparent drops to land on whitespace.
        if (Array.from(e.dataTransfer.types).includes(SUBTASK_SOURCE_MIME)) {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        }
        onWrapperDragOver(e);
      }}
      onDragLeave={onWrapperDragLeave}
      onDrop={(e) => {
        setDropLine(null);
        // Subtask-source dropped on whitespace = unparent. Row/RoomBand drop
        // handlers already call e.stopPropagation() after consuming their MIME,
        // so by the time we get here, the drop is on grid whitespace.
        const raw = e.dataTransfer.getData(SUBTASK_SOURCE_MIME);
        if (raw && onUnparentSubtasks) {
          try {
            const payload = JSON.parse(raw);
            const taskIds = [
              ...(payload.task_ids || []),
              // Subtasks always travel as task_ids (ft:* in the row-key, mapped
              // to task ids in buildRoomMovePayload).
            ];
            if (taskIds.length) {
              e.preventDefault();
              onUnparentSubtasks(taskIds);
            }
          } catch (err) { console.warn(err); }
        }
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: gridTemplate,
          minWidth: fixedTotal + crewTotal,
        }}
      >
        <GridHeader
          visibleCrew={visibleCrew}
          leftOffsets={leftOffsets}
          beginColResize={beginColResize}
          onCrewDragStart={onCrewDragStart}
          onCrewDragOver={onCrewDragOver}
          onCrewDrop={onCrewDrop}
          onCrewDragEnd={onCrewDragEnd}
          groupMode={groupMode}
        />

        {virtEnabled && topPx > 0 && (
          <div
            aria-hidden
            style={{ gridColumn: "1 / -1", height: topPx }}
            data-testid="virt-spacer-top"
          />
        )}

        {rows.slice(visibleStart, visibleEnd).map((r) => {
          if (r.kind === "date") {
            const onsiteTime = project?.settings?.days?.[r.dateKey]?.onsite_time;
            return (
              <DateBand
                key={`d-${r.dateKey}`}
                label={r.label}
                colSpan={colSpan}
                dateKey={r.dateKey}
                onDropFloatingForDay={onDropFloatingForDay}
                onOpenDaySettings={onOpenDaySettings}
                onsiteTime={onsiteTime}
              />
            );
          }
          if (r.kind === "room") {
            return (
              <RoomBand
                key={`r-${r.room_id || "unassigned"}`}
                room={r.room}
                colSpan={colSpan}
                taskCount={r.taskCount}
                scheduledCount={r.scheduledCount}
                floatingCount={r.floatingCount}
                onRoomDrop={onMoveSelectionToRoom}
              />
            );
          }
          if (r.kind === "floating-divider") {
            return (
              <div
                key={`fd-${r.room_id || "u"}`}
                style={{ gridColumn: `1 / span ${colSpan}`, height: 20 }}
                className="border-b border-line-subtle flex items-center px-3 mono text-2xs uppercase tracking-[0.2em] text-amber/70 bg-bg-base/60 sticky left-0"
                data-testid={`floating-divider-${r.room_id || "u"}`}
              >
                — UNSCHEDULED · {r.count} —
              </div>
            );
          }
          if (r.kind === "floating-task") {
            const key = `ft:${r.task.id}`;
            return (
              <FloatingTaskRow
                key={`ft-${r.task.id}`}
                task={r.task}
                room={r.room}
                rowH={rowH}
                leftOffsets={leftOffsets}
                colSpan={colSpan}
                isSelected={selection.includes(key)}
                onSelect={(e) => handleSelect(key, e)}
                onSelectTask={onSelectTask}
                onDuplicateTask={onDuplicateTask}
                onDeleteTask={onDeleteTask}
                onPatchTask={onPatchTask}
                onEditNotes={onEditNotes}
                visibleCrew={visibleCrew}
                assignments={assignments}
                onAssignCrewToTask={onAssignCrewToTask}
                onUnassignCrew={onUnassignCrew}
                selection={selection}
                selectionRef={selectionRef}
                onGroupWithTask={onGroupTasks}
                crewRoles={project?.crew_roles}
                onAddCrewRole={onAddCrewRole}
              />
            );
          }
          if (r.kind === "date-sub") {
            return (
              <div
                key={`ds-${r.room_id || "u"}-${r.dateKey}`}
                style={{ gridColumn: `1 / span ${colSpan}`, height: 20 }}
                className="border-b border-line-subtle flex items-center px-3 mono text-2xs uppercase tracking-[0.2em] text-ink-mute bg-bg-base/60 sticky left-0"
                data-testid={`date-sub-${r.room_id || "u"}-${r.dateKey}`}
              >
                — {r.label} —
              </div>
            );
          }
          if (r.kind === "newtask") {
            const isRoomMode = groupMode === "room";
            return (
              <NewTaskRow
                key={`nt-${isRoomMode ? (r.room_id || "u") : r.dateKey}`}
                mode={isRoomMode ? "room" : "date"}
                dateKey={r.dateKey}
                roomId={r.room_id}
                colSpan={colSpan}
                leftWidth={fixedTotal}
                onOpenDialog={(dk, rid) => {
                  if (isRoomMode) {
                    onNewFloatingForRoom && onNewFloatingForRoom(rid);
                  } else {
                    onNewTaskForDay && onNewTaskForDay(dk);
                  }
                }}
                onQuickAdd={async (title) => {
                  if (isRoomMode) {
                    if (onQuickAddFloatingForRoom) await onQuickAddFloatingForRoom(r.room_id, title);
                  } else if (onQuickAddTask) {
                    await onQuickAddTask(r.dateKey, title);
                  }
                }}
              />
            );
          }
          if (r.kind === "subtask") {
            return (
              <SubtaskRow
                key={`s-${r.subtask.id}`}
                subtask={r.subtask}
                room={roomById[r.parent_block.room_id]}
                rowH={Math.max(24, rowH - 8)}
                leftOffsets={leftOffsets}
                crew={visibleCrew}
                onPatchTask={onPatchTask}
                onOpenHistory={() => onOpenHistory(r.parent_block, r.subtask)}
                onSelectTask={() => onSelectTask && onSelectTask(r.subtask, r.parent_block)}
              />
            );
          }

          const block = r.block;
          const t = taskById[block.task_id];
          const room = roomById[block.room_id];
          const ba = assignmentsByBlock[block.id] || [];
          const bb = blockersByTask[block.task_id] || [];
          const subs = subtasksByParent[block.task_id] || [];

          // Drag/resize preview offsets.
          const isDraggingRow = dragState?.blockId === block.id && dragState.newStartAt;
          const isResizingRow = resizeState?.blockId === block.id && resizeState.deltaMin !== 0;
          const isPreviewing = isDraggingRow || isResizingRow;
          const startDisplay = isDraggingRow ? dragState.newStartAt : block.start_at;
          const endDisplay = isDraggingRow
            ? isoNoMs(
                new Date(
                  new Date(dragState.newStartAt).getTime() +
                    (new Date(dragState.origEnd) - new Date(dragState.origStart))
                )
              )
            : isResizingRow
              ? isoNoMs(addMinutes(resizeState.origEnd, resizeState.deltaMin))
              : block.end_at;
          const dur = diffMin(startDisplay, endDisplay);

          return (
            <Row
              key={block.id}
              block={block}
              task={t}
              room={room}
              ba={ba}
              bb={bb}
              subs={subs}
              isExpanded={expanded.has(block.task_id)}
              onToggleExpand={() => toggleExpanded(block.task_id)}
              crew={visibleCrew}
              rowH={rowH}
              leftOffsets={leftOffsets}
              isSelected={selection.includes(block.id)}
              isPreviewing={isPreviewing}
              startDisplay={startDisplay}
              endDisplay={endDisplay}
              dur={dur}
              driftMin={block.drift_min || 0}
              taskStatus={t?.status || "planned"}
              onSelect={(e) => handleSelect(block.id, e)}
              onDragHandle={(e) => beginRowDrag(block, e)}
              onResize={(e) => beginResize(block, e)}
              onDragOver={handleRowDragOver}
              onDrop={(e) => handleRowDrop(block, e)}
              onPatchTask={onPatchTask}
              onPatchBlock={onPatchBlock}
              onAssignCrew={onAssignCrew}
              onUnassignCrew={onUnassignCrew}
              doubleBookKeys={doubleBookKeys}
              onOpenHistory={() => onOpenHistory(block, t)}
              onSelectTask={() => onSelectTask && onSelectTask(t, block)}
              onEditNotes={() => onEditNotes && onEditNotes(t)}
              onDuplicateTask={onDuplicateTask}
              onDeleteTask={onDeleteTask}
              rooms={rooms}
              roomById={roomById}
              crewTimelines={crewTimelines}
              groupMode={groupMode}
              selection={selection}
              selectionRef={selectionRef}
              onGroupWithTask={onGroupTasks}
              crewRoles={project?.crew_roles}
              onAddCrewRole={onAddCrewRole}
            />
          );
        })}

        {virtEnabled && bottomPx > 0 && (
          <div
            aria-hidden
            style={{ gridColumn: "1 / -1", height: bottomPx }}
            data-testid="virt-spacer-bottom"
          />
        )}
      </div>

      <NowLineOverlay nowRowIndex={nowRowIndex} rows={rows} rowH={rowH} headerH={32} />

      {dragState?.newStartAt && (
        <DragBadge
          mouseY={dragState.mouseY}
          deltaMin={dragState.deltaMin}
          toTime={dragState.newStartAt}
          kind="snap"
          anchorTitle={
            (taskById[blocks.find((b) => b.id === dragState.anchorBlockId)?.task_id]?.title) || ""
          }
        />
      )}
      {(resizeState?.deltaMin !== undefined && resizeState.deltaMin !== 0) && (
        <DragBadge
          mouseY={resizeState.mouseY}
          deltaMin={resizeState.deltaMin}
          toTime={isoNoMs(addMinutes(resizeState.origEnd, resizeState.deltaMin))}
          kind="resize"
        />
      )}

      {/* Drop-line overlay — renders during crew column reorders or floating
          task drags. Vertical line for column reorders, horizontal for row
          drops. Pointer events disabled so it doesn't interfere with the
          underlying drop targets. */}
      {dropLine && dropLine.kind === "crew-col" && (
        <div
          aria-hidden
          className="absolute top-0 bottom-0 pointer-events-none z-40"
          style={{
            left: dropLine.x,
            width: 2,
            background: "#FFB300",
            boxShadow: "0 0 8px rgba(255,179,0,0.9)",
          }}
          data-testid="drop-line-crew"
        />
      )}
      {dropLine && dropLine.kind === "floating-row" && (
        <div
          aria-hidden
          className="absolute left-0 right-0 pointer-events-none z-40"
          style={{
            top: dropLine.y,
            height: 2,
            background: "#FFB300",
            boxShadow: "0 0 8px rgba(255,179,0,0.9)",
          }}
          data-testid="drop-line-floating"
        />
      )}

      {/* Row-drag horizontal drop-line — appears while a schedule block is
          being dragged to a new time. The line sits at the cursor Y so the
          producer can see exactly where the row will snap to. We use fixed
          positioning to escape any wrapper transforms / scroll. */}
      {dragState && dragState.deltaMin !== undefined && (
        <div
          aria-hidden
          className="fixed left-0 right-0 pointer-events-none z-30"
          style={{
            top: dragState.mouseY,
            height: 2,
            background: "#FFB300",
            boxShadow: "0 0 8px rgba(255,179,0,0.9)",
          }}
          data-testid="drop-line-row"
        />
      )}
    </div>
  );
}

